import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Breadcrumb.css';

// Turn a URL slug into a human-readable title, e.g. "why-book-direct" -> "Why Book Direct".
const slugToTitle = (slug) =>
    slug
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

/**
 * Breadcrumb component for SEO and navigation
 * Implements Schema.org BreadcrumbList for rich snippets
 */
const Breadcrumb = () => {
    const location = useLocation();

    // Define breadcrumb mapping for static pages
    const breadcrumbMap = {
        '/about': { name: 'About Us', path: '/about' },
        '/contact': { name: 'Contact', path: '/contact' },
        '/booking': { name: 'Book Your Stay', path: '/booking' },
        '/search': { name: 'Search', path: '/search' },
        '/privacy': { name: 'Privacy Policy', path: '/privacy' },
        '/terms': { name: 'Terms & Conditions', path: '/terms' },
        '/faqs': { name: 'FAQs', path: '/faqs' },
        '/frequently-asked-questions': { name: 'FAQs', path: '/frequently-asked-questions' },
        '/blog': { name: 'Blog', path: '/blog' },
        '/gallery': { name: 'Gallery', path: '/gallery' },
        '/rentals/newry-maine': { name: 'Newry, Maine', path: '/rentals/newry-maine' },
        '/rentals/bethel-maine': { name: 'Bethel, Maine', path: '/rentals/bethel-maine' }
    };

    // Map property slugs to their display names
    const propertyNames = {
        'frost-pine-chalet': 'Frost Pine Chalet'
    };

    // Build breadcrumb trail
    const buildBreadcrumbs = () => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const breadcrumbs = [{ name: 'Home', path: '/', position: 1 }];

        let currentPath = '';
        pathSegments.forEach((segment, index) => {
            currentPath += `/${segment}`;
            const breadcrumbInfo = breadcrumbMap[currentPath];

            if (breadcrumbInfo) {
                breadcrumbs.push({
                    name: breadcrumbInfo.name,
                    path: breadcrumbInfo.path,
                    position: breadcrumbs.length + 1
                });
            } else if (pathSegments[index - 1] === 'property') {
                // Property detail page - show the property name (no standalone /property page)
                breadcrumbs.push({
                    name: propertyNames[segment] || 'Property',
                    path: currentPath,
                    position: breadcrumbs.length + 1
                });
            } else if (pathSegments[index - 1] === 'blog') {
                // Blog post detail - derive a readable title from the slug.
                breadcrumbs.push({
                    name: slugToTitle(segment) || 'Article',
                    path: currentPath,
                    position: breadcrumbs.length + 1
                });
            }
        });

        return breadcrumbs;
    };

    const breadcrumbs = buildBreadcrumbs();

    // Don't show on homepage
    if (location.pathname === '/') {
        return null;
    }

    // Schema.org BreadcrumbList structured data
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb) => ({
            "@type": "ListItem",
            "position": crumb.position,
            "name": crumb.name,
            "item": `https://www.timbrluxstays.com${crumb.path}`
        }))
    };

    return (
        <nav className="breadcrumb" aria-label="Breadcrumb navigation">
            {/* Schema.org JSON-LD */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
            />

            <ol className="breadcrumb-list" itemScope itemType="https://schema.org/BreadcrumbList">
                {breadcrumbs.map((crumb, index) => (
                    <li
                        key={crumb.path}
                        className="breadcrumb-item"
                        itemProp="itemListElement"
                        itemScope
                        itemType="https://schema.org/ListItem"
                    >
                        {index < breadcrumbs.length - 1 ? (
                            <>
                                <Link
                                    to={crumb.path}
                                    itemProp="item"
                                    className="breadcrumb-link"
                                >
                                    <span itemProp="name">{crumb.name}</span>
                                </Link>
                                <meta itemProp="position" content={crumb.position} />
                                <span className="breadcrumb-separator" aria-hidden="true">/</span>
                            </>
                        ) : (
                            <>
                                <span
                                    className="breadcrumb-current"
                                    itemProp="name"
                                    aria-current="page"
                                >
                                    {crumb.name}
                                </span>
                                <meta itemProp="position" content={crumb.position} />
                            </>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
};

export default Breadcrumb;
